import React, { useEffect, useState } from 'react'
import { PlusOutlined } from '@ant-design/icons'
import { Button, Image, Modal, Upload, UploadFile, notification } from 'antd'
import styled from 'styled-components'
import axios from 'axios'

import { apiUrl } from '../../globals'
import { UploadCloud } from 'react-feather'
import { FlexRow } from '../FlexboxReusables/FlexItems'

const MAX_IMAGES = 4 // CHANGE ME

type ImageUploadModalProps = {
  showImageUploadModal: boolean
  setShowImageUploadModal: (show: boolean) => void
  courseId: number
  questionId: number
  answerId: number | null
  imageType: 'question' | 'answer'
}

export const ImageUploadModal = ({
  showImageUploadModal,
  setShowImageUploadModal,
  courseId,
  questionId,
  answerId,
  imageType
}: ImageUploadModalProps) => {
  const [ previewOpen, setPreviewOpen ] = useState(false)
  const [ previewImage, setPreviewImage ] = useState('')
  const [ fileList, setFileList ] = useState<Array<UploadFile>>([])

  useEffect(() => {
    if (!showImageUploadModal) {
      setPreviewOpen(false)
      setPreviewImage('')
      setFileList([])
    }
  }, [ showImageUploadModal ])

  const handleChange = ({ fileList: newFileList }: { fileList: Array<UploadFile> }) => {
    setFileList(newFileList)
  }

  const handleUpload = async () => {
    const uploads = fileList.map(async (file) => {
      if ((file.status === 'uploading' || file.status === 'error') && file.originFileObj) {
        const base64Image = await getBase64(file.originFileObj)

        const payload = {
          CourseId: courseId,
          ImageFile: base64Image,
          ImageType: imageType,
          QuestionId: questionId,
          AnswerId: answerId,
        }

        try {
          await axios.post(`${apiUrl}/upload-image`, payload)
          file.status = 'done'
          file.url = URL.createObjectURL(file.originFileObj)

          notification.success({ // i8n
            message: 'Upload erfolgreich',
            description: 'Das Bild wurde erfolgreich hochgeladen.',
            placement: 'bottomRight',
          })
          setShowImageUploadModal(false)
        } catch (error) {
          console.error('Upload failed:', error)
          file.status = 'error'
        }
      }
      return file
    })

    Promise.all(uploads).then(setFileList)
  }

  const uploadButton = (
    <UploadButton type="text">
      <PlusOutlined />
      <FlexRow>
        <UploadText>Hinzufügen</UploadText>
      </FlexRow>
    </UploadButton>
  )

  return (
    <Modal
      open={showImageUploadModal}
      onCancel={() => setShowImageUploadModal(false)}
      onOk={handleUpload}
      okText={
        <FlexRow>
          <UploadCloud size={16} style={{ margin: '0 0.5em 0 0' }} />
          Upload
        </FlexRow>
      }
      cancelText="Abbrechen"
      centered
    >
      <Upload
        listType="picture-card"
        fileList={fileList}
        onChange={handleChange}
      >
        {fileList.length >= MAX_IMAGES ? null : uploadButton}
      </Upload>
      {previewImage && (
        <Image
          style={{ display: 'none' }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible: boolean) => !visible 
              ? setPreviewImage('')
              : setPreviewOpen(visible)
          }}
          src={previewImage}
        />
      )}
    </Modal>
  )
}

const getBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const base64String = (reader.result as string).split(',')[1]
      resolve(base64String)
    }
    reader.onerror = (error) => reject(error)
  })


const UploadButton = styled(Button)`
  border: 0;
  width: 50%;
  background: 'none';
  margin-bottom: 22px !important;
`

const UploadText = styled.div`
  margin-top: 8px;
`